import angular from "angular";
import {
  IFieldArray,
  IFieldConfigurationObject,
  IFieldGroup,
  IFormOptionsAPI,
} from "AngularFormly";
import { PatchObject, Record } from "../../server/crud";
import {
  PatchDoubleClickHandlerConfig,
  CellDoubleClickedEventHandlerArgs,
  PatchFieldConfiguration,
} from "./models";

import { default as formlyUtilities } from "../../../ui-formly/formUtilities";
import { ICrudRecordResponse } from "../../server/query";
import { default as fieldHelpers } from "../../../ui-formly/fieldHelpers";

export interface PatchModalControllerResult {
  patched: boolean;
  resultRecord?: Record;
  patchObject?: PatchObject;
  selectedItem?: Record;
}

export class PatchModalController {
  static $inject = [
    "$uibModalInstance",
    "patchDoubleClickHandlerConfig",
    "patchDoubleClickHandlerArgs",
  ];

  httpPending: boolean = false;
  form: angular.IFormController | undefined;
  formFields: AngularFormly.IFieldArray;
  patchObject: PatchObject;
  options: IFormOptionsAPI;

  constructor(
    private readonly $uibModalInstance: angular.ui.bootstrap.IModalInstanceService,
    private readonly config: PatchDoubleClickHandlerConfig,
    private readonly args: CellDoubleClickedEventHandlerArgs
  ) {
    this.options = {
      formState: {
        record: args.data,
      },
    };
    this.patchObject =
      this.config.getInitialPatchObject?.call(
        undefined,
        this.args,
        this.config
      ) ?? this.getInitialPatchObject(this.args, this.config);

    const patchFields = this.getPatchFields(config);

    const formlyFieldArray = patchFields.reduce<IFieldArray>(
      (result, current) => [...result, ...this.getFieldsForPatchField(current)],
      []
    );
    const headerField = this.getHeaderField();
    this.formFields = [headerField, ...formlyFieldArray];
  }

  private getPatchFields(
    config: PatchDoubleClickHandlerConfig
  ): PatchFieldConfiguration[] {
    const patchFields: PatchFieldConfiguration | PatchFieldConfiguration[] =
      config.patchField instanceof Function
        ? config.patchField(this.args)
        : config.patchField;
    const patchFieldArray =
      patchFields instanceof Array ? patchFields : [patchFields];

    return patchFieldArray;
  }

  private getHeaderField(): IFieldConfigurationObject {
    return {
      template: `
      <div class="col-xs-4">
        <label>Field</label>
      </div>
      <div class="col-xs-4">
        <label>New Value</label>
      </div>
      <div class="col-xs-4">
        <label>Current Value</label>
      </div>
      `,
      className: "col-xs-12",
    };
  }

  private getFieldsForPatchField(
    patchField: PatchFieldConfiguration
  ): IFieldGroup[] {
    const fields: IFieldGroup[] = fieldHelpers.columnLayout(
      [
        this.getLabelField(patchField),
        patchField.formField,
        this.getReadOnlyReferenceField(patchField)!,
      ],
      3
    );

    return fields;
  }

  getRecordName(): string {
    return (
      this.config.getRecordName?.call(undefined, this.args) ??
      this.args.data.Name ??
      this.args.data.Id?.toString() ??
      "unknown"
    );
  }

  submit() {
    formlyUtilities.clearServerErrors(this.form);

    if (this.form?.$pristine) {
      this.closeWithNoResult();
      return;
    }

    if (this.form?.$valid) {
      this.httpPending = true;
      this.config
        .submit(this.patchObject)
        .then((response: unknown) => {
          this.form?.$setPristine();
          const crudResponse = <ICrudRecordResponse<Record>>response;
          this.closeWithRecordResult({
            patched: true,
            resultRecord: crudResponse.Result,
            patchObject: this.patchObject,
          });
        })
        .catch((result) => {
          this.httpPending = false;
          if (result.status === 400) {
            formlyUtilities.setServiceStackServerErrors(
              result.data.ResponseStatus.Errors,
              this.form
            );
          } else {
            throw result;
          }
        });
    }
  }

  cancel() {
    this.closeWithNoResult();
  }

  private getLabelField(
    patchField: PatchFieldConfiguration
  ): IFieldConfigurationObject {
    const label = patchField.formField.templateOptions?.label ?? "Unknown";
    return {
      template: `<div class="form-control-static">${label}</div>`,
    };
  }

  private getReadOnlyReferenceField(
    patchField: PatchFieldConfiguration
  ): IFieldConfigurationObject | undefined {
    const referenceFieldName =
      patchField.referenceFieldName ?? patchField.formField.key?.toString();

    if (!referenceFieldName) {
      return undefined;
    }

    const formatters = (patchField.referenceFieldFormatters ?? []).map(
      (f) => (record: unknown) => f(record)
    );

    return {
      ...fieldHelpers.simpleReadonlyField({
        key: referenceFieldName,
        name: referenceFieldName,
        label: "Current Value",
        model: this.args.data,
        formatters,
      }),
      type: "gridReadonly",
    };
  }

  private closeWithNoResult() {
    const result: PatchModalControllerResult = {
      patchObject: undefined,
      patched: false,
      selectedItem: undefined,
      resultRecord: undefined,
    };
    this.$uibModalInstance.close(result);
  }

  private closeWithRecordResult(result: PatchModalControllerResult) {
    this.$uibModalInstance.close(result);
  }

  private getInitialPatchObject(
    args: CellDoubleClickedEventHandlerArgs,
    config: PatchDoubleClickHandlerConfig
  ): PatchObject {
    const data = args.data;
    const patchObject: PatchObject = {
      Id: data.Id,
    };

    const patchFields = this.getPatchFields(config);
    patchFields.forEach((pf) => {
      const key = pf.formField.key;
      if (key) {
        patchObject[key] = data[key];
      }
    });

    return patchObject;
  }
}
